<script setup lang="ts">
const userStore = useUserStore()
const baseStore = useBaseStore()
const consentStore = useConsentStore()

const config = useRuntimeConfig()
const localePath = useLocalePath()
const route = useRoute()
const toast = useToast()

// Promo data
const promo = computed(() => baseStore.promos)
const promoPercentage = computed(() => {
  if (promo.value?.percentage) {
    const percentage = Number.parseFloat(promo.value.percentage)
    return Math.round(percentage)
  }
  return null
})

// Computed properties for dynamic styling
const promoBackground = computed(() => {
  if (promo.value?.banner_bg_from && promo.value?.bg_from_to) {
    return `linear-gradient(135deg, ${promo.value.banner_bg_from} 0%, ${promo.value.bg_from_to} 100%)`
  }
  return 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff4757 100%)'
})

const promoTextColor = computed(() => promo.value?.title_color || '#FFFFFF')

const newsletterCheck = ref(0)
const isNewsletterLoading = ref(false)
const newsletterEmail = ref('')
const honeypot = ref('')

const menuItems = computed(() => [{
  label: 'Accueil',
  to: localePath({ name: 'IndexPage' }),
  icon: 'i-custom-menu-home',
  active: route.name === 'IndexPage___fr',
}, {
  label: 'Recettes',
  to: localePath({ name: 'RecipesPage' }),
  icon: 'i-custom-menu-recipes',
  active: route.name === 'RecipesPage___fr' || route.name === 'RecipePage___fr' || route.name === 'MoneySaverPage___fr',
}, {
  label: 'Circulaires',
  to: localePath({ name: 'FlyersPage' }),
  icon: 'i-custom-menu-flyers',
  active: route.name === 'FlyersPage___fr' || route.name === 'FlyerPage___fr' || route.name === 'TrackedIngredientsPage___fr',
}, {
  label: 'Essence',
  to: localePath({ name: 'GasPricePage' }),
  icon: 'i-custom-menu-gas',
  active: route.name === 'GasPricePage___fr',
}, {
  label: 'Listes',
  to: localePath({ name: 'ShoppingListPage' }),
  icon: 'i-custom-menu-lists',
  active: route.name === 'ShoppingListPage___fr' || route.name === 'RecipeBoxPage___fr' || route.name === 'MealPlannerPage___fr',
}])

async function handleNewsletterSubmit() {
  const email = newsletterEmail.value.trim()

  // Honeypot check - if this field is filled, it's a bot
  if (honeypot.value.trim() !== '') {
    // Silently fail for bots
    return
  }

  // Simple email format check
  const isEmailValid = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(email)
  if (email.length < 5 || !isEmailValid) {
    toast.add({
      title: 'Adresse courriel invalide',
      description: 'Veuillez entrer une adresse courriel valide.',
      icon: 'i-lucide-alert-circle',
      color: 'error',
    })
    return
  }

  try {
    isNewsletterLoading.value = true
    newsletterCheck.value = 1

    const response = await $fetch(`${config.public.apiUrl}/home/subscribe-newsletter`, {
      method: 'POST',
      params: {
        email,
        validator: newsletterCheck.value,
      },
    })

    isNewsletterLoading.value = false

    if (response.success === 1) {
      toast.add({
        title: 'Inscription réussie',
        description: 'Vérifiez vos courriels pour confirmer votre inscription.',
        icon: 'i-lucide-check-circle',
        color: 'success',
      })
      newsletterEmail.value = ''
      honeypot.value = ''
    }
    else {
      toast.add({
        title: 'Erreur',
        description: 'Une erreur est survenue lors du traitement de votre demande.',
        icon: 'i-lucide-alert-circle',
        color: 'error',
      })
    }
  }
  catch {
    isNewsletterLoading.value = false
  }
}
</script>

<template>
  <div>
    <!-- Safe area background extension -->

    <div id="bottom-bar" class="bg-default/85 backdrop-blur fixed bottom-0 left-0 w-full xl:hidden safe-area-bottom" style="z-index: 50;">
      <div v-if="promo && promoPercentage && baseStore.shouldShowPromos" class="absolute hidden bottom-full left-0 w-full pointer-events-none">
        <div class="promo-badge text-center text-xs w-1/5 ml-auto rounded-t-lg py-0.5 shadow-lg relative overflow-hidden" :style="{ background: promoBackground, color: promoTextColor }">
          Promo -{{ promoPercentage }}%
          <!-- Effet de brillance -->
          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-pulse" />
        </div>
      </div>
      <div class="flex items-center justify-between pt-2 h-15 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_4px_6px_-1px_rgba(0,0,0,0.1)]">
        <ULink
          v-for="item in menuItems"
          :key="item.label"
          :to="item.to"
          class="text-center w-full flex flex-col items-center h-full transition-colors duration-300 ease-in-out"
          :class="item.active
            ? 'border-b-3 border-tomate text-tomate fill-tomate'
            : 'border-b-3 border-transparent text-poivre fill-poivre'"
        >
          <UIcon
            :name="item.icon"
            class="size-10 h-7.5 margin-auto fill-current transition-colors duration-300 ease-in-out"
          />
          <span class="text-xs mt-[1px] transition-colors duration-300 ease-in-out">
            {{ item.label }}
          </span>
        </ULink>
      </div>
    </div>
    <div
      v-if="(route.name != 'FlyerPage___fr')"
      class="h-18 bg-sel xl:hidden safe-area-bottom"
    />

    <div v-if="route.name != 'FlyerPage___fr'" class="hidden xl:block pl-55 bg-sel">
      <div v-if="!userStore.user || (userStore.user && !userStore.user.extra)" class="px-4 mx-auto max-w-7xl sm:px-6 xl:px-8 mb-8 print:hidden pt-4">
        <div class="relative flex items-center px-6 py-12 mt-0 sm:py-6 sm:px-10">
          <div class="absolute inset-0 overflow-hidden rounded-lg">
            <img src="https://glouton.b-cdn.net/images/jumpy-salad-bowl-min.jpg" loading="lazy" alt="" class="sm:hidden object-cover object-center w-full h-full filter saturate-0">
            <img src="https://glouton.b-cdn.net/images/jumpy-salad-bowl-min-large.jpg" loading="lazy" alt="" class="hidden sm:inline object-cover object-center w-full h-full filter saturate-0">
            <div class="absolute inset-0 bg-tomate/90" />
          </div>
          <div class="relative mx-auto text-center md:text-left grid grid-cols-1 md:grid-cols-2 content-between">
            <div class=" text-white uppercase v-full md:w-85 grid grid-cols-1 gap-0 mb-5 md:mb-0">
              <span class="text-4xl">
                Glouton
              </span>
              <span class="text-4xl font-bold">
                en mode turbo
              </span>
            </div>

            <div class=" text-white max-w-130 grid grid-cols-1 gap-0 content-between">
              <p class="mb-2">
                Débloquez toutes les fonctionnalités et profitez de Glouton sans publicité. Essayez gratuitement pour 14 jours, aucune carte de crédit n'est requise.
              </p>
              <p>
                <ULink class="text-lg md:text-base font-bold text-white whitespace-nowrap hover:text-poivre-200" :to="$localePath({ name: 'ExtraPage' })">
                  Découvrez l'Extra<span aria-hidden="true"> &rarr;</span>
                </ULink>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div id="footer" aria-labelledby="footer-heading" class="pb-20 lg:pb-10 relative pt-10 bg-sauge print:hidden" style="">
        <div class="mx-auto max-w-7xl px-8">
          <div class="container mx-auto">
            <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-y-8 gap-x-4 xl:gap-y-8 xl:gap-x-8">
              <!-- Première rangée -->
              <div class="col-span-2 sm:col-span-3">
                <div class="mb-4">
                  <img class="h-8 w-auto shrink-0" src="/images/logo-white.svg">
                </div>
                <h3 class="font-title text-2xl font-bold mb-2 text-sorbet tracking-wide">
                  L'infolettre, pour ne rien manquer!
                </h3>
                <div class="w-full sm:w-100">
                  <p class="text-white text-sm mb-4">
                    Recevez les meilleures aubaines des épiceries ainsi que les nouveautés directement dans vos courriels chaque semaine.
                  </p>

                  <div class="flex mt-4">
                    <UInput
                      v-model="newsletterEmail"
                      placeholder="Votre adresse courriel..."
                      type="email"
                      autocomplete="email"
                      class="w-full"
                      :ui="{ base: 'rounded-full py-3 px-4 text-base' }"
                      @keydown.enter="handleNewsletterSubmit"
                    />

                    <!-- Honeypot field - hidden from users -->
                    <input
                      v-model="honeypot"
                      type="text"
                      name="firstname"
                      style="display:none"
                      autocomplete="off"
                      aria-hidden="true"
                      tabindex="-1"
                    >

                    <UButton type="submit" size="xl" class="ml-2 px-3" :loading="isNewsletterLoading" icon="i-mdi-email-outline" :ui="{ icon: 'p-3' }" @click="handleNewsletterSubmit" />
                  </div>
                </div>
              </div>
              <div class="col-span-1 text-white">
                <h3 class="text-sm font-bold mb-4 uppercase">
                  À propos
                </h3>
                <ul class="text-sm space-y-4">
                  <li>
                    <ULink as="a" :to="localePath({ name: 'PrivacyPolicyPage' })" class="hover:underline text-white hover:text-white">
                      Politique de confidentialité
                    </ULink>
                  </li>
                  <li>
                    <ULink as="a" :to="localePath({ name: 'TermsOfUsePage' })" class="hover:underline text-white hover:text-white">
                      Conditions d'utilisation
                    </ULink>
                  </li>
                  <li v-if="baseStore.platform == 'ios'">
                    <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" class="hover:underline text-white">Conditions d'utilisation d'Apple</a>
                  </li>
                  <li>
                    <button type="button" class="hover:underline text-white text-left" @click="consentStore.openCustomize()">
                      Préférences de confidentialité
                    </button>
                  </li>
                  <li><a href="https://support.glouton.app/fr/category/glouton-pour-entreprises-z9i55a/" target="_blank" class="hover:underline text-white">Services aux entreprises</a></li>
                  <li>
                    <ULink as="a" :to="localePath({ name: 'CombatPage' })" class="hover:underline text-white hover:text-white">
                      Combat des circulaires
                    </ULink>
                  </li>
                </ul>
              </div>
              <div class="col-span-1 text-white">
                <h3 class="text-sm font-bold mb-4 uppercase">
                  Abonnement Extra
                </h3>
                <ul class="text-sm space-y-4">
                  <li>
                    <ULink :to="localePath({ name: 'ExtraPage' })" class="hover:underline text-white hover:text-white">
                      Gratuit vs. Extra
                    </ULink>
                  </li>
                  <li><a href="https://support.glouton.app/fr/article/comment-fonctionne-lessai-gratuit-c9hvsu/" target="_blank" class="hover:underline text-white">Essai gratuit de 14 jours</a></li>
                </ul>
              </div>

              <div class="col-span-1 text-white">
                <h3 class="text-sm font-bold mb-4 uppercase">
                  Contact
                </h3>
                <ul class="text-sm space-y-4">
                  <li><a href="https://support.glouton.app" target="_blank" class="hover:underline text-white">Centre d'aide</a></li>
                  <li><a href="#" class="hover:underline text-white" @click.prevent="baseStore.openCrispChat()">Support en direct <UIcon name="i-mdi-chat-outline" /></a></li>
                </ul>
              </div>
            </div>

            <!-- Deuxième rangée -->
          </div>
          <div class="py-10 sm:flex sm:items-center sm:justify-between">
            <div class="flex flex-col items-center justify-center md:mt-0">
              <div class="flex space-x-8 text-white">
                <a href="https://facebook.com/glouton.cuisine" target="_blank" title="Facebook">
                  <UIcon name="i-mdi-facebook" class="size-10" />
                </a>
                <a href="https://instagram.com/glouton.app" target="_blank" title="Instagram">
                  <UIcon name="i-mdi-instagram" class="size-10" />
                </a>
                <a href="https://www.pinterest.ca/glouton_app" target="_blank" title="Pinterest">
                  <UIcon name="i-mdi-pinterest" class="size-10" />
                </a>
                <a href="https://www.youtube.com/c/GloutonApp" target="_blank" title="Youtube">
                  <UIcon name="i-mdi-youtube" class="size-10" />
                </a>
                <a href="https://www.tiktok.com/@glouton.app" target="_blank" title="TikTok">
                  <UIcon name="i-ic-baseline-tiktok" class="size-10" />
                </a>
              </div>
            </div>
            <div class="flex flex-col items-center justify-center mt-4 sm:mt-0">
              <div v-if="baseStore.platform == 'web'" class="flex space-x-4 ">
                <a href="https://go.glouton.app/app-ios" target="_blank" class="block">
                  <img src="https://glouton.b-cdn.net/images/badges/apple_badge.png" class="h-10">
                </a>
                <a href="https://go.glouton.app/app-android" target="_blank" class="block">
                  <img src="https://glouton.b-cdn.net/images/badges/android_badge.png" class="h-10">
                </a>
              </div>
            </div>
          </div>
          <div>
            <p class="text-sm text-white">
              &copy; 2025 Glouton - Tous droits réservés (v{{ baseStore.version }} {{ baseStore.platform }}) <span v-if="baseStore.platform == 'ios'"> - <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank">Conditions d'utilisation d'Apple</a> </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.animate-pulse {
  animation: shimmer 3s ease-in-out infinite;
}
</style>
